/**
 * ui.ts — the live HUD side panel.
 *
 * Built imperatively (no framework). Shown during a running episode, holds:
 *   - belief swatch + bars for R, G, B, size        §6.2
 *   - fish-preference legend (true swatch + 6 editable sliders)
 *   - catch stats (weight kg, count, rate %) + rolling rate chart
 *   - cumulative regret chart
 *   - round counter
 *
 * Update model: the main loop calls `panel.update(state)` once per bandit
 * round. The legend sliders fire callbacks into the main loop immediately.
 * Start-time hyperparams (rounds, speed, window, β, drift) live in the start
 * dialogue, not here.
 */

import {
  type AnchorLayout,
  marginalWeights,
  R_BLOCK,
  G_BLOCK,
  B_BLOCK,
  SIZE_BLOCK,
} from './featurize';
import { type Action } from './featurize';

/** Snapshot the main loop pushes to the panel after each bandit round. */
export interface PanelState {
  round: number;
  cumulativeRegret: number;
  catches: number;
  casts: number;
  weightKg: number;
  recentCatches: boolean[];
  weights: Float64Array;
  layout: AnchorLayout;
  agentColor: { r: number; g: number; b: number };
  agentSize: number;
  truePreference: Action;
  /** Exploration β currently in effect (reward-adaptive). Shown live so the
   *  viewer sees exploration settle as the agent locks on, climb back when
   *  the fish drift. */
  beta: number;
}

/** Legend slider callbacks — fires when the viewer redirects the fish. */
export interface PanelCallbacks {
  onEditPreference: (p: Partial<Action>) => void;
}

const COLORS = {
  bad: '#c44e52',
  good: '#55a868',
  neutral: '#4c72b0',
  dim: '#8a9bab',
  panel: '#0f2230',
  panelLine: '#1d3a4f',
  text: '#dfe9ef',
};

export class SidePanel {
  readonly el: HTMLElement;
  private regretCanvas: HTMLCanvasElement;
  private rateCanvas: HTMLCanvasElement;
  private swatchEl: HTMLElement;
  private trueSwatchEl: HTMLElement;
  private sizeBar: HTMLElement;
  private colorBars: Record<'r' | 'g' | 'b', HTMLElement>;
  private statRound: HTMLElement;
  private statBeta: HTMLElement;
  private statRegret: HTMLElement;
  private statCatches: HTMLElement;
  private statWeight: HTMLElement;
  private statRate: HTMLElement;
  private regretHistory: number[] = [];

  constructor(cb: PanelCallbacks) {
    this.el = document.createElement('aside');
    this.el.className = 'panel';
    this.el.innerHTML = `
      <header class="panel-head">
        <h1>Bandit Fisher</h1>
        <p class="tagline">A learning agent finds where the fish are.</p>
      </header>

      <section class="block">
        <div class="block-title">Catch</div>
        <div class="stat-row big"><span>weight</span><span id="stat-weight">0.0 kg</span></div>
        <div class="stat-row"><span>caught</span><span id="stat-catches">0 / 0</span></div>
        <div class="stat-row"><span>rate</span><span id="stat-rate">0%</span></div>
        <canvas class="chart" id="rate-chart" height="60"></canvas>
      </section>

      <section class="block">
        <div class="block-title">Cumulative regret</div>
        <canvas class="chart" id="regret-chart" height="80"></canvas>
        <div class="stat-row"><span>regret</span><span id="stat-regret">0.00</span></div>
      </section>

      <section class="block">
        <div class="block-title">Agent's belief</div>
        <div class="belief-swatch-wrap">
          <div class="belief-swatch" id="swatch"></div>
          <div class="belief-label">best-guess bait colour</div>
        </div>
        <div class="bars">
          <div class="bar-row"><span class="bar-name">R</span><div class="bar" id="bar-r"></div></div>
          <div class="bar-row"><span class="bar-name">G</span><div class="bar" id="bar-g"></div></div>
          <div class="bar-row"><span class="bar-name">B</span><div class="bar" id="bar-b"></div></div>
          <div class="bar-row"><span class="bar-name">size</span><div class="bar" id="bar-size"></div></div>
        </div>
      </section>

      <section class="block">
        <div class="block-title">Fish preference (true) — drag to redirect</div>
        <div class="belief-swatch-wrap">
          <div class="belief-swatch" id="true-swatch"></div>
          <div class="belief-label">bait colour the fish want</div>
        </div>
        <div class="slider-row">
          <label>location X <span id="val-tx">0.50</span></label>
          <input type="range" id="sl-tx" min="0" max="1" step="0.01" value="0.5" />
        </div>
        <div class="slider-row">
          <label>location Y <span id="val-ty">0.50</span></label>
          <input type="range" id="sl-ty" min="0" max="1" step="0.01" value="0.5" />
        </div>
        <div class="slider-row">
          <label>bait R <span id="val-tr">0.50</span></label>
          <input type="range" id="sl-tr" min="0" max="1" step="0.01" value="0.5" />
        </div>
        <div class="slider-row">
          <label>bait G <span id="val-tg">0.50</span></label>
          <input type="range" id="sl-tg" min="0" max="1" step="0.01" value="0.5" />
        </div>
        <div class="slider-row">
          <label>bait B <span id="val-tb">0.50</span></label>
          <input type="range" id="sl-tb" min="0" max="1" step="0.01" value="0.5" />
        </div>
        <div class="slider-row">
          <label>bait size <span id="val-ts">0.50</span></label>
          <input type="range" id="sl-ts" min="0" max="1" step="0.01" value="0.5" />
        </div>
      </section>

      <section class="block">
        <div class="block-title">Live</div>
        <div class="stat-row"><span>round</span><span id="stat-round">0</span></div>
        <div class="stat-row"><span>explore β</span><span id="stat-beta">—</span></div>
      </section>

      <footer class="panel-foot">
        <p>Each cast, the agent updates a linear model of where fish bite. The white crosshair is where it thinks the fish are; the dotted ring is where they actually are.</p>
      </footer>
    `;

    // Legend sliders — let the viewer steer the hidden preference and watch
    // the agent re-learn. Each fires onEditPreference with the patched field.
    const wirePref = (
      inputId: string,
      valId: string,
      key: keyof Action,
    ) => {
      const input = this.el.querySelector<HTMLInputElement>(`#${inputId}`)!;
      const val = this.el.querySelector<HTMLElement>(`#${valId}`)!;
      input.addEventListener('input', () => {
        const v = parseFloat(input.value);
        val.textContent = v.toFixed(2);
        cb.onEditPreference({ [key]: v });
      });
    };
    wirePref('sl-tx', 'val-tx', 'x');
    wirePref('sl-ty', 'val-ty', 'y');
    wirePref('sl-tr', 'val-tr', 'r');
    wirePref('sl-tg', 'val-tg', 'g');
    wirePref('sl-tb', 'val-tb', 'b');
    wirePref('sl-ts', 'val-ts', 'size');

    this.regretCanvas = this.el.querySelector<HTMLCanvasElement>('#regret-chart')!;
    this.rateCanvas = this.el.querySelector<HTMLCanvasElement>('#rate-chart')!;
    this.swatchEl = this.el.querySelector<HTMLElement>('#swatch')!;
    this.trueSwatchEl = this.el.querySelector<HTMLElement>('#true-swatch')!;
    this.sizeBar = this.el.querySelector<HTMLElement>('#bar-size')!;
    this.colorBars = {
      r: this.el.querySelector<HTMLElement>('#bar-r')!,
      g: this.el.querySelector<HTMLElement>('#bar-g')!,
      b: this.el.querySelector<HTMLElement>('#bar-b')!,
    };
    this.statRound = this.el.querySelector<HTMLElement>('#stat-round')!;
    this.statBeta = this.el.querySelector<HTMLElement>('#stat-beta')!;
    this.statRegret = this.el.querySelector<HTMLElement>('#stat-regret')!;
    this.statCatches = this.el.querySelector<HTMLElement>('#stat-catches')!;
    this.statWeight = this.el.querySelector<HTMLElement>('#stat-weight')!;
    this.statRate = this.el.querySelector<HTMLElement>('#stat-rate')!;
  }

  /** Reset charts and stats for a fresh episode. */
  reset(): void {
    this.regretHistory = [];
    const rc = this.regretCanvas.getContext('2d')!;
    rc.clearRect(0, 0, this.regretCanvas.width, this.regretCanvas.height);
    const rcc = this.rateCanvas.getContext('2d')!;
    rcc.clearRect(0, 0, this.rateCanvas.width, this.rateCanvas.height);
  }

  /** Called once per bandit round with the latest snapshot. */
  update(s: PanelState): void {
    this.statRound.textContent = String(s.round);
    this.statBeta.textContent = s.beta.toFixed(3);
    this.statRegret.textContent = s.cumulativeRegret.toFixed(2);
    this.statCatches.textContent = `${s.catches} / ${s.casts}`;
    this.statWeight.textContent = `${s.weightKg.toFixed(1)} kg`;
    const rate = s.casts > 0 ? (s.catches / s.casts) * 100 : 0;
    this.statRate.textContent = `${rate.toFixed(0)}%`;

    const { r, g, b } = s.agentColor;
    this.swatchEl.style.background = `rgb(${Math.floor(r * 255)},${Math.floor(g * 255)},${Math.floor(b * 255)})`;

    renderBar(this.colorBars.r, s.weights, s.layout, R_BLOCK, `rgb(${(r * 255) | 0},0,0)`);
    renderBar(this.colorBars.g, s.weights, s.layout, G_BLOCK, `rgb(0,${(g * 255) | 0},0)`);
    renderBar(this.colorBars.b, s.weights, s.layout, B_BLOCK, `rgb(0,0,${(b * 255) | 0})`);
    renderBar(this.sizeBar, s.weights, s.layout, SIZE_BLOCK, COLORS.neutral);

    // True-preference legend: swatch + sync the sliders to the drifting
    // values. If a slider is being dragged, don't yank it out from under the
    // user — `document.activeElement` is a coarse "editing now" signal.
    const tp = s.truePreference;
    this.trueSwatchEl.style.background = `rgb(${Math.floor(tp.r * 255)},${Math.floor(tp.g * 255)},${Math.floor(tp.b * 255)})`;
    const sync = (inputId: string, valId: string, v: number) => {
      const input = this.el.querySelector<HTMLInputElement>(`#${inputId}`)!;
      if (document.activeElement === input) return;
      input.value = v.toFixed(2);
      this.el.querySelector<HTMLElement>(`#${valId}`)!.textContent = v.toFixed(2);
    };
    sync('sl-tx', 'val-tx', tp.x);
    sync('sl-ty', 'val-ty', tp.y);
    sync('sl-tr', 'val-tr', tp.r);
    sync('sl-tg', 'val-tg', tp.g);
    sync('sl-tb', 'val-tb', tp.b);
    sync('sl-ts', 'val-ts', tp.size);

    this.regretHistory.push(s.cumulativeRegret);
    if (this.regretHistory.length > 300) this.regretHistory.shift();
    drawLineChart(this.regretCanvas, this.regretHistory, COLORS.bad);

    const ys = s.recentCatches.map((c) => (c ? 1 : 0));
    drawLineChart(this.rateCanvas, ys, COLORS.good, { floor: 0 });
  }
}

/**
 * Render a marginal feature's belief as a row of vertical segments, one per
 * anchor. The top-weighted segment is highlighted; the rest are dimmed.
 */
function renderBar(
  container: HTMLElement,
  weights: Float64Array,
  layout: AnchorLayout,
  block: number,
  highlightColor: string,
): void {
  const { weights: ws } = marginalWeights(block, weights, layout);
  const max = Math.max(...ws.map(Math.abs), 1e-6);
  let bestIdx = 0;
  let bestVal = -Infinity;
  ws.forEach((w, i) => {
    if (w > bestVal) {
      bestVal = w;
      bestIdx = i;
    }
  });
  if (container.children.length !== ws.length) {
    container.innerHTML = '';
    ws.forEach(() => {
      const seg = document.createElement('div');
      seg.className = 'bar-seg';
      container.appendChild(seg);
    });
  }
  ws.forEach((w, i) => {
    const seg = container.children[i] as HTMLElement;
    const mag = Math.abs(w) / max;
    seg.style.height = `${Math.max(4, mag * 100)}%`;
    seg.style.background = i === bestIdx ? highlightColor : COLORS.dim;
    seg.style.opacity = i === bestIdx ? '1' : `${0.3 + mag * 0.5}`;
  });
}

/** Tiny line chart on a canvas. */
function drawLineChart(
  canvas: HTMLCanvasElement,
  ys: number[],
  color: string,
  opts?: { floor?: number },
): void {
  const dpr = window.devicePixelRatio || 1;
  const cssW = canvas.clientWidth || 200;
  const cssH = canvas.clientHeight || (canvas.height || 60);
  if (canvas.width !== Math.floor(cssW * dpr)) {
    canvas.width = Math.floor(cssW * dpr);
    canvas.height = Math.floor(cssH * dpr);
  }
  const ctx = canvas.getContext('2d')!;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, cssW, cssH);

  if (ys.length < 2) return;
  let lo = Math.min(...ys);
  let hi = Math.max(...ys);
  if (opts?.floor !== undefined) {
    lo = opts.floor;
    if (hi < lo) hi = lo + 1;
  } else if (hi - lo < 1e-6) {
    hi = lo + 1;
  }
  const x = (i: number) => (i / (ys.length - 1)) * cssW;
  const y = (v: number) => cssH - ((v - lo) / (hi - lo)) * (cssH - 4) - 2;

  ctx.strokeStyle = COLORS.panelLine;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(0, y(opts?.floor ?? lo));
  ctx.lineTo(cssW, y(opts?.floor ?? lo));
  ctx.stroke();

  ctx.strokeStyle = color;
  ctx.lineWidth = 1.8;
  ctx.beginPath();
  for (let i = 0; i < ys.length; i++) {
    if (i === 0) ctx.moveTo(x(i), y(ys[i]));
    else ctx.lineTo(x(i), y(ys[i]));
  }
  ctx.stroke();

  ctx.lineTo(x(ys.length - 1), y(opts?.floor ?? lo));
  ctx.lineTo(x(0), y(opts?.floor ?? lo));
  ctx.closePath();
  ctx.fillStyle = color + '22';
  ctx.fill();
}
